import { ArrowDown, ArrowUp, CheckCircle2, Copy, GripVertical, Pencil, Search, Trash2, WandSparkles } from "lucide-react";
import { useDeferredValue, useState } from "react";
import type { PlaylistDocument, PlaylistEntry, ValidationIssue } from "@protv-converter/core";
import { EntryEditor } from "./EntryEditor";

interface PlaylistEditorProps {
  document: PlaylistDocument;
  selectedId: string | null;
  issues: ValidationIssue[];
  onHeaderChange: (header: string) => void;
  onSelect: (entryId: string | null) => void;
  onUpdate: (entryId: string, patch: Partial<PlaylistEntry>) => void;
  onRemove: (entryId: string) => void;
  onMove: (entryId: string, direction: -1 | 1) => void;
  onClear: () => void;
  onDeduplicate: () => void;
  onCopyPreview: () => void;
}

export function PlaylistEditor({
  document,
  selectedId,
  issues,
  onHeaderChange,
  onSelect,
  onUpdate,
  onRemove,
  onMove,
  onClear,
  onDeduplicate,
  onCopyPreview,
}: PlaylistEditorProps) {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query.trim().toLowerCase());
  const issueMap = new Map<string, ValidationIssue[]>();
  for (const issue of issues) issueMap.set(issue.entryId, [...(issueMap.get(issue.entryId) ?? []), issue]);
  const filteredEntries = deferredQuery
    ? document.entries.filter((entry) => `${entry.title} ${entry.mainUrl} ${entry.tags.join(" ")}`.toLowerCase().includes(deferredQuery))
    : document.entries;
  const selected = document.entries.find((entry) => entry.id === selectedId) ?? null;

  return (
    <section aria-labelledby="editor-title" className="flex h-full min-h-0 flex-col bg-paper">
      <div className="flex flex-wrap items-center gap-2 border-b border-line px-4 py-3">
        <div className="mr-auto min-w-44">
          <p className="eyebrow">Playlist editor</p>
          <h2 id="editor-title" className="sr-only">Playlist editor</h2>
        </div>
        <button className="toolbar-button" type="button" onClick={onDeduplicate} disabled={document.entries.length < 2}><WandSparkles aria-hidden="true" size={15} /> Remove duplicates</button>
        <button className="toolbar-button" type="button" onClick={onCopyPreview} disabled={document.entries.length === 0}><Copy aria-hidden="true" size={15} /> Copy output</button>
        <button className="toolbar-button text-danger" type="button" onClick={onClear} disabled={document.entries.length === 0}><Trash2 aria-hidden="true" size={15} /> Clear</button>
      </div>

      <div className="grid gap-3 border-b border-line bg-canvas/55 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_minmax(15rem,0.55fr)]">
        <div>
          <label className="sr-only" htmlFor="playlist-header">Playlist header</label>
          <input id="playlist-header" className="field bg-paper" value={document.header} onChange={(event) => onHeaderChange(event.target.value)} placeholder="Playlist header (optional)" />
        </div>
        <div className="relative">
          <Search aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted" size={16} />
          <label className="sr-only" htmlFor="playlist-search">Search playlist</label>
          <input id="playlist-search" className="field bg-paper pl-9" type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search title, URL, or tag" />
        </div>
      </div>

      {document.entries.length === 0 ? (
        <div className="grid flex-1 place-items-center px-6 py-16 text-center">
          <div className="max-w-md">
            <p className="font-display text-3xl font-medium tracking-tight text-ink">Start with the media, not the formatting.</p>
            <p className="mt-3 text-sm leading-6 text-muted">Load a YouTube playlist, paste direct media links, or import an existing ProTV file. Version-specific formatting happens when you export.</p>
          </div>
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="sticky top-0 z-10 hidden grid-cols-[2.5rem_2.75rem_minmax(10rem,1fr)_minmax(11rem,1.2fr)_7rem_6rem] border-b border-line bg-paper px-3 py-2 text-[0.68rem] font-semibold uppercase tracking-[0.1em] text-muted md:grid">
            <span /><span>#</span><span>Title</span><span>URL</span><span>Status</span><span className="text-right">Actions</span>
          </div>

          {filteredEntries.map((entry) => {
            const index = document.entries.findIndex((candidate) => candidate.id === entry.id);
            const entryIssues = issueMap.get(entry.id) ?? [];
            const hasError = entryIssues.some((issue) => issue.level === "error");
            const hasWarning = entryIssues.some((issue) => issue.level === "warning");
            return (
              <div className="playlist-row" key={entry.id}>
                <div className={`grid min-h-16 items-center gap-2 border-b px-3 py-2 md:grid-cols-[2.5rem_2.75rem_minmax(10rem,1fr)_minmax(11rem,1.2fr)_7rem_6rem] ${selectedId === entry.id ? "border-accent/35 bg-accent-soft" : "border-line bg-paper hover:bg-canvas/70"}`}>
                  <GripVertical aria-hidden="true" className="hidden text-muted md:block" size={17} />
                  <span className="hidden text-xs tabular-nums text-muted md:block">{index + 1}</span>
                  <button className="min-w-0 cursor-pointer text-left" type="button" onClick={() => onSelect(selectedId === entry.id ? null : entry.id)}>
                    <span className="block truncate text-sm font-semibold text-ink">{entry.title || "Untitled media"}</span>
                    <span className="mt-0.5 block truncate text-xs text-muted md:hidden">{entry.mainUrl}</span>
                  </button>
                  <span className="hidden truncate font-mono text-[0.7rem] text-muted md:block" title={entry.mainUrl}>{entry.mainUrl}</span>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${hasError ? "text-danger" : hasWarning ? "text-warning" : "text-valid"}`}>
                    <CheckCircle2 aria-hidden="true" size={14} /> {hasError ? "Error" : hasWarning ? "Check" : "Ready"}
                  </span>
                  <div className="flex justify-end gap-0.5">
                    <button className="icon-button" type="button" title={`Move ${entry.title || "entry"} up`} onClick={() => onMove(entry.id, -1)} disabled={index === 0} aria-label={`Move ${entry.title || "entry"} up`}><ArrowUp aria-hidden="true" size={15} /></button>
                    <button className="icon-button" type="button" title={`Move ${entry.title || "entry"} down`} onClick={() => onMove(entry.id, 1)} disabled={index === document.entries.length - 1} aria-label={`Move ${entry.title || "entry"} down`}><ArrowDown aria-hidden="true" size={15} /></button>
                    <button className="icon-button" type="button" title={`Edit ${entry.title || "entry"}`} onClick={() => onSelect(entry.id)} aria-label={`Edit ${entry.title || "entry"}`}><Pencil aria-hidden="true" size={15} /></button>
                    <button className="icon-button text-danger" type="button" title={`Remove ${entry.title || "entry"}`} onClick={() => onRemove(entry.id)} aria-label={`Remove ${entry.title || "entry"}`}><Trash2 aria-hidden="true" size={15} /></button>
                  </div>
                </div>
                {selected?.id === entry.id ? (
                  <EntryEditor entry={selected} issues={entryIssues} onChange={(patch) => onUpdate(entry.id, patch)} onClose={() => onSelect(null)} />
                ) : null}
              </div>
            );
          })}

          {filteredEntries.length === 0 ? <p className="px-5 py-10 text-center text-sm text-muted">No entries match “{query}”.</p> : null}
        </div>
      )}

      <div className="flex items-center justify-between border-t border-line bg-canvas/55 px-4 py-2.5 text-xs text-muted">
        <span>{document.entries.length} {document.entries.length === 1 ? "entry" : "entries"}{query ? ` · ${filteredEntries.length} shown` : ""}</span>
        <span>{issues.filter((issue) => issue.level === "error").length} blocking · {issues.filter((issue) => issue.level === "warning").length} notices</span>
      </div>
    </section>
  );
}
